import { DetailTextProps } from 'business/models/CustomModels';
import {
  ContainerDropDTO,
  CrystalDTO,
  IBaseDriverPropertyDTO,
  IBasePropertyDTO,
  IBooleanPropertyDTO,
  ICaptureProfileResultDTO,
  ICaptureProfilesDTO,
  IColorPropertyDTO,
  IContainerDropDTO,
  IContainersDTO,
  IDBListPropertyDTO,
  IDispensingRobotDTO,
  IDoublePropertyDTO,
  IDropDTO,
  IDropLayerDTO,
  IDropLayerFragment,
  IExperimentImagingScheduleDTO,
  IExperimentImagingScheduleEntryDTO,
  IExperimentInfoDTO,
  IExperimentInfoImagingScheduleDTO,
  IExperimentPlateDTO,
  IExperimentScreenLot,
  IFloatPropertyDTO,
  IHistogramDropRegions,
  IISOWellDropScoreDTO,
  IIngredient,
  IIngredientStock,
  IIntPropertyDTO,
  IListEnumDTO,
  IListPropertyDTO,
  ILongPropertyDTO,
  IMorphologyDetail,
  IPlateCrystalIdentifiers,
  IPlateDropRegions,
  IPlateIngredientDTO,
  IPlatesDTO,
  IProteinFormulationDTO,
  IProteinFormulationInfoDTO,
  IProteinFormulationIngredientsDTO,
  IProteinFormulationListDTO,
  IProteinFormulationStandardIngredientsDTO,
  IRegionCaptureProfileDTO,
  ISaveExprimentInfoDTO,
  ISaveImagingScheduleDTO,
  IScoreDetail,
  IScoresOfWellDropsDTO,
  IScreenLotDTO,
  IScreenLotPlateDTO,
  IScribblePointDTO,
  IStringPropertyDTO,
  IVendor,
  IWellAndDropNumberDTO,
  IWellDrawingDetailDTO,
  IWellIngDTO,
  IWellLayerDTO,
  IWellLayerIngredientDTO,
  InspectionSetDTO,
} from 'business/models/Models';
import { EProductEditions } from 'enums/EProductEditions';

export class CrystalClientDTO extends CrystalDTO {
  color: string;
  zIndex?: number;
  locked: boolean;
}

export enum DisplayMode {
  IMAGE = 'IMAGE',
  DESIGN = 'DESIGN',
  SCORE = 'SCORE',
  IMAGESCORE = 'IMAGESCORE',
  WELLIMAGE = 'WELLIMAGE',
  EXPINFO = 'EXPINFO',
  SCREEN = 'SCREEN',
  REPORT = 'REPORT',
  WELLINGREDIENT = 'WELLINGREDIENT',
}

export interface PlateImageStructure {
  plateId: number;
  plateNumber: number;
  startX: number;
  startY: number;
  width: number;
  height: number;
  imageCompositeKeyString: string;
}

export interface ExperimentGridDTO {
  rowCount?: number;
  plateRowCount?: number;
  colCount?: number;
  plateColCount?: number;
}

export interface DetailExperimentDTO {
  containerCols?: number;
  containerRows?: number;
  containerDrops?: ContainerDropDTO[];
  containerMaxDropVolume?: number;
  containerMaxWellVolume?: number;
  containerDefSeedVolume?: number;
  containerDefaultFragmentVolume?: number;
  colCount?: number;
  rowCount?: number;
  deletedPlateIds?: number[];
  plateDto?: Plate[];
  defWellVolume?: number;
  isDirty?: boolean;
}

export interface DetailExpInfoDTO {
  name?: string;
  notes?: string;
  noteBookPageNum?: any;
  setupTemperature?: any;
  setupTempId?: any;
  reducingAgent?: any;
  incubTemperature?: any;
  incubTempId?: any;
  isDirty?: any;
  userName?: any;
  userId?: any;
  plateRowCount?: number;
  plateColCount?: number;
}

export interface ScreenLot extends Omit<IScreenLotDTO, 'experiment'> {}

export interface ScreenLotPlate extends Omit<IScreenLotPlateDTO, 'screenLot'> {
  screenLot: ScreenLot;
}

export interface ContainerDrop extends Omit<IContainerDropDTO, 'container'> {}

export interface VendorDto extends Omit<IVendor, 'ingredientStock'> {}

export interface IngredientStockDto
  extends Omit<IIngredientStock, 'ingredient' | 'liquidClass' | 'vendor'> {
  vendor: VendorDto;
}

export interface IngredientDto
  extends Omit<
    IIngredient,
    | 'bufferCalculationType'
    | 'bufferDataPoint'
    | 'casnumber'
    | 'ingredientAlias'
    | 'ingredientIngredientType'
    | 'ingredientStock'
    | 'randomLayerExcludeListIngredient1'
    | 'randomLayerExcludeListIngredient2'
  > {}

export interface WellLayerIngredientDto
  extends Omit<
    IWellLayerIngredientDTO,
    | 'ingredientStock'
    | 'ingredientStockIngredient'
    | 'highPhIngStock'
    | 'highPhIngStockIngredient'
  > {
  ingredientStock: IngredientStockDto;
  ingredientStockIngredient: IngredientDto;
  highPhIngStock: IngredientStockDto;
  highPhIngStockIngredient: IngredientDto;
}
export interface WellLayer
  extends Omit<IWellLayerDTO, 'layer' | 'wellLayerIngredientList'> {
  wellLayerIngredientList: WellLayerIngredientDto[];
}

export interface DropLayer
  extends Omit<IDropLayerDTO, 'dropLayerIngredientList' | 'dropLayerFragmentList'> {
  dropLayerIngredientList?: WellLayerIngredientDto[];
  dropLayerFragmentList?: IDropLayerFragment[];
}

export interface Drop extends Omit<IDropDTO, 'dropLayerList'> {
  dropLayerList: DropLayer[];
}

export interface WellIng extends Omit<IWellIngDTO, 'wellLayerList' | 'dropList'> {
  wellLayerList: WellLayer[];
  dropList: Drop[];
}

export interface ProteinFormulationList
  extends Omit<
    IProteinFormulationListDTO,
    'proteinInfo' | 'ingredients' | 'standardIngredients'
  > {
  proteinInfo: IProteinFormulationInfoDTO;
  ingredients: IProteinFormulationIngredientsDTO[];
  standardIngredients: IProteinFormulationStandardIngredientsDTO[];
}

export interface PlateIngredient
  extends Omit<
    IPlateIngredientDTO,
    'proteinFormulation' | 'wellList' | 'containerDropList'
  > {
  containerDropList: ContainerDrop[];
  wellList: WellIng[];
  proteinFormulation?: { [key: string]: ProteinFormulationList } | undefined;
}

export interface ScoreDetail extends Omit<IScoreDetail, 'imageBatchWellDropScore'> {}
export interface MorphologyDetail
  extends Omit<IMorphologyDetail, 'imageBatchWellDropScore'> {}

export interface ScoresOfWellDrops
  extends Omit<IScoresOfWellDropsDTO, 'scoreDetails' | 'morphologyDetails'> {
  scoreDetails: ScoreDetail[];
  morphologyDetails: MorphologyDetail[];
}

export interface IScribblesData {
  scribbleStartPointsXYofWell?: IScribblePointDTO | undefined;
  regionScribblePointsArray?: number[] | undefined;
  xWidthyHeightCaptureResult?: ICaptureProfileResultDTO | undefined;
}

export interface PlateScribble {
  plateScribbles?: IScribblesData[] | undefined;
  wellNumsContainingScribbles?: IWellAndDropNumberDTO[] | undefined;
}
export interface Annotations {
  plateScribblesDetail?: PlateScribble | undefined;
  plateScores?: ScoresOfWellDrops[][] | undefined;
  plateROI?: IWellDrawingDetailDTO[] | undefined;
  plateAnnotations?: IWellDrawingDetailDTO[] | undefined;
  plateDropRegions?: IPlateDropRegions[] | undefined;
  plateHistograms?: IHistogramDropRegions[] | undefined;
  plateCrystalIdentifiers?: IPlateCrystalIdentifiers[] | undefined;
}
export interface Plate
  extends Omit<
    IPlatesDTO,
    | 'experimentPlate'
    | 'screenLotPlate'
    | 'ingredients'
    | 'isoScores'
    | 'canvasColumnPosition'
    | 'canvasRowPosition'
    | 'experimentID'
    | 'hasErrors'
    | 'hasWarning'
    | 'isQueued'
    | 'numCols'
    | 'numRows'
    | 'state'
    | 'treeNodeID'
  > {
  experimentPlate?: IExperimentPlateDTO;
  screenLotPlate?: ScreenLotPlate;
  ingredients: PlateIngredient;
  isoScores?: IISOWellDropScoreDTO[];
  ingredientsRequested?: boolean;
  preRenderRequested?: boolean;
  wellRects?: Array<DetailTextProps>;
  image?: any;
  wellRectsPadded?: any;
  startX?: number;
  startY?: number;
  availableWidth?: number;
  availableHeight?: number;
  width?: number;
  height?: number;
  stx?: number;
  sty?: number;
  endx?: number;
  endy?: number;
  annotationLoaded?: boolean;
  tunedImage?: any;
  plateScores?: ScoresOfWellDrops[][];
  drawingIdentifiers?: Annotations;
  state?: number;
}

export interface SaveExperimentInfo extends Omit<ISaveExprimentInfoDTO, 'plates'> {
  plates: Plate[];
}

export interface ProteinFormulationDto
  extends Omit<IProteinFormulationDTO, 'pfIng' | 'pfStIng'> {
  pfIng?: IProteinFormulationIngredientsDTO[] | undefined;
  pfStIng?: IProteinFormulationStandardIngredientsDTO[] | undefined;
}

export interface PlateDrawingIdentifiers {
  drawingsIdentifier?: Annotations | undefined;
  plateId: number;
}
export interface ExperimentInfoDto
  extends Omit<IExperimentInfoDTO, 'screenLots' | 'dispensingProps'> {
  screenLots?: IExperimentScreenLot[];
  isDirty?: boolean;
}

export interface ExperimentInfoImagingScheduleDto
  extends Omit<
    IExperimentInfoImagingScheduleDTO,
    'imagingSchedules' | 'imagingScheduleEntries'
  > {
  imagingSchedules?: IExperimentImagingScheduleDTO[] | undefined;
  imagingScheduleEntries?: IExperimentImagingScheduleEntryDTO[] | undefined;
}

export interface SaveImagingSchedule
  extends Omit<ISaveImagingScheduleDTO, 'imagingScheduleEntries'> {
  imagingScheduleEntries?: IExperimentImagingScheduleEntryDTO[] | undefined;
}

export interface Containers extends Omit<IContainersDTO, 'containerDrops'> {
  containerDrops?: ContainerDrop[] | undefined;
}

export enum PropertyType {
  BooleanProperty,
  StringProperty,
  DBListProperty,
  ListProperty,
  IntProperty,
  LongProperty,
  ColorProperty,
  DoubleProperty,
  ListEnum,
  FloatProperty,
}

export interface BaseProperty extends IBasePropertyDTO {
  type: PropertyType;
}
export interface BooleanProperty extends IBooleanPropertyDTO, BaseProperty {}

export interface StringProperty extends IStringPropertyDTO, BaseProperty {}

export interface DBListProperty extends IDBListPropertyDTO, BaseProperty {}

export interface ListProperty extends IListPropertyDTO, BaseProperty {}

export interface IntProperty extends IIntPropertyDTO, BaseProperty {}

export interface LongProperty extends ILongPropertyDTO, BaseProperty {}

export interface ColorProperty extends IColorPropertyDTO, BaseProperty {}

export interface DoubleProperty extends IDoublePropertyDTO, BaseProperty {}

export interface ListEnum extends IListEnumDTO, BaseProperty {}

export interface FloatProperty extends IFloatPropertyDTO, BaseProperty {}

export type Property =
  | BooleanProperty
  | StringProperty
  | DBListProperty
  | ListProperty
  | IntProperty
  | LongProperty
  | ColorProperty
  | DoubleProperty
  | ListEnum
  | FloatProperty;
export interface BaseDriverProperty extends Omit<IBaseDriverPropertyDTO, 'property'> {
  property?: Property;
}

export interface ExperimentDispensingProperties {
  robots?: IDispensingRobotDTO[] | undefined;
  properties?: { [key: string]: Property[] } | undefined;
}

export interface PlateCompositeImage {
  compositeImageKey: string;
  plateId: number;
  image: any;
}

export interface RegionCaptureProfilesDto
  extends Omit<IRegionCaptureProfileDTO, 'captureProfile'> {
  captureProfile?: ICaptureProfilesDTO;
}

export enum DropUrlParameters {
  PLATEID = 3,
  WELLNUM = 4,
  DROPNUMBER = 5,
  CPID = 6,
  REGIONNAME = 7,
  IMAGEBATCHID = 8,
}
export enum WellSettingParameter {
  cpId = 'cpId',
  dropNumber = 'dropNumber',
  inspections = 'inspections',
  regionName = 'regionName',
}

export type DropImageParameters = {
  dropNumber: number;
  cpId: number;
  regionName: string;
  inspection: InspectionSetDTO;
};

export type WellNavigationDetails = {
  wellNum: number;
  plateNum: number;
  plateID?: number;
  selectionOrder?: number;
};

export const ExperimentCanvasRoutes = [
  DisplayMode.DESIGN,
  DisplayMode.IMAGE,
  DisplayMode.SCORE,
  DisplayMode.WELLINGREDIENT,
];

export const ScreenExperimentCanvasRoutes = [
  DisplayMode.DESIGN,
  DisplayMode.WELLINGREDIENT,
];

export const isScoringFeatureEnabled = (productEdition?: EProductEditions): boolean => {
  return productEdition !== EProductEditions.ExperimentDesigner;
};

export function getExperimentCanvasRoutes(
  isPlateGenerated: boolean,
  isImagedExperiment: boolean,
  rmLite: boolean,
  productEdition?: EProductEditions,
): DisplayMode[] {
  const routes: DisplayMode[] = [];

  //DO NOT CHANGE THE ORDER OF THESE CONDITIONS, IT MATTERS, IT SHOULD ALWAYS BE PUSHED IN ARRAY IN THIS ORDER
  if (!rmLite) {
    routes.push(DisplayMode.DESIGN);
  }
  if (
    isScoringFeatureEnabled(productEdition) &&
    ((rmLite && !isPlateGenerated) || isPlateGenerated || isImagedExperiment)
  ) {
    routes.push(DisplayMode.IMAGE);
  }
  routes.push(DisplayMode.WELLINGREDIENT);
  if (isPlateGenerated && isScoringFeatureEnabled(productEdition)) {
    routes.push(DisplayMode.SCORE);
  }
  return routes;
}

export enum CutCopyOperationType {
  Cut,
  Copy,
}

export const WIDTH_MAIN_DIV = 100;

export const ExperimentSideBarWidth = 60;

export type WellIngredientInfo = {
  conc: number;
  concUnit: string;
  shortName: string;
  longName: string;
  pH: number;
  type: string;
  ingredientColor?: number;
};

export type WellIngredientDropInfoDetails = {
  plateID: number;
  plateBarcode: string;
  wellName: string;
  dropNumber: number;
  dropWellVolume: number;
  dropProteinVolume: number;
  wellIngredients: WellIngredientInfo[];
};

export type ExperimentCanvasRoute =
  | DisplayMode.DESIGN
  | DisplayMode.IMAGE
  | DisplayMode.SCORE
  | DisplayMode.WELLINGREDIENT;

export const MaximumPlateWells = 6145;

export const RotatedScreenNameRegex = {
  name: /(?:_Rot180(?:_\d+)?)+$/i,
};
